export const WEEKDAY_SALES_TIME_ZONE = 'Europe/Riga'
export const WEEKDAY_SHAPE_HISTORY_DAYS = 740

export enum WeekdayKey {
  Monday = 'monday',
  Tuesday = 'tuesday',
  Wednesday = 'wednesday',
  Thursday = 'thursday',
  Friday = 'friday',
  Saturday = 'saturday',
  Sunday = 'sunday',
}

export enum WeekdaySalesMetric {
  Orders = 'orders',
  Turnover = 'turnover',
  AverageCheck = 'average_check',
}

export enum WeekdayShapePreset {
  OneMonth = 'one_month',
  ThreeMonths = 'three_months',
  TwelveMonths = 'twelve_months',
  Custom = 'custom',
}

export enum WeekdayEvidenceQuality {
  Sufficient = 'sufficient',
  SmallSample = 'small_sample',
  Insufficient = 'insufficient',
}

export interface WeekdaySalesDailyAggregate {
  date: string
  orders: number
  turnoverKopecks: number
  incomeKopecks: number | null
}

export interface WeekdayDateRange { from: string; to: string }
export interface WeekdaySalesCoverage { from: string; to: string }

export interface WeekdayShapePoint {
  weekday: WeekdayKey
  occurrences: number
  daysWithSales: number
  orders: number
  turnoverKopecks: number
  value: number
  previousValue: number | null
  changeRate: number | null
  index: number
  share: number
  smallSample: boolean
}

export interface WeekdayShapeAvailable {
  availability: 'available'
  metric: WeekdaySalesMetric
  range: WeekdayDateRange
  previousRange: WeekdayDateRange
  points: WeekdayShapePoint[]
  totalOrders: number
  totalTurnoverKopecks: number
  calendarDays: number
  observedDays: number
  daysWithSales: number
  quality: WeekdayEvidenceQuality
  peakWeekday: WeekdayKey
}

export interface WeekdayShapeUnavailable {
  availability: 'unavailable'
  reason: 'invalid_range' | 'range_outside_coverage' | 'no_sales'
  metric: WeekdaySalesMetric
  range: WeekdayDateRange
}

export type WeekdayShape = WeekdayShapeAvailable | WeekdayShapeUnavailable

export interface WeekdayForecastProfile {
  availability: 'available' | 'unavailable'
  reason?: 'invalid_range' | 'range_outside_coverage' | 'no_sales' | 'insufficient_shape_evidence' | 'coverage_unavailable'
  range: WeekdayDateRange
  indexes?: Record<WeekdayKey, number>
  quality: WeekdayEvidenceQuality
  observedDays: number
  totalOrders: number
}

const WEEKDAYS = [
  WeekdayKey.Monday,
  WeekdayKey.Tuesday,
  WeekdayKey.Wednesday,
  WeekdayKey.Thursday,
  WeekdayKey.Friday,
  WeekdayKey.Saturday,
  WeekdayKey.Sunday,
] as const
const ISO_DATE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/

function parseDate(value: string): Date | null {
  if (!ISO_DATE.test(value)) return null
  const date = new Date(`${value}T12:00:00Z`)
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value ? date : null
}
function dateKey(value: Date): string { return value.toISOString().slice(0, 10) }
export function addCalendarDays(value: string, days: number): string {
  const date = parseDate(value)
  if (!date) return ''
  date.setUTCDate(date.getUTCDate() + days)
  return dateKey(date)
}
export function weekdayRangeDays(range: WeekdayDateRange): number {
  const from = parseDate(range.from), to = parseDate(range.to)
  if (!from || !to || from > to) return 0
  return Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1
}
function weekdayForDate(value: string): WeekdayKey | null {
  const date = parseDate(value)
  if (!date) return null
  return WEEKDAYS[(date.getUTCDay() + 6) % 7]
}
function shiftMonth(value: string, months: number): string {
  const date = parseDate(value)
  if (!date) return ''
  const originalDay = date.getUTCDate()
  date.setUTCDate(1)
  date.setUTCMonth(date.getUTCMonth() + months)
  const lastDay = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate()
  date.setUTCDate(Math.min(originalDay, lastDay))
  return dateKey(date)
}

export function weekdayPresetRange(anchor: string, preset: Exclude<WeekdayShapePreset, WeekdayShapePreset.Custom>): WeekdayDateRange {
  const months = preset === WeekdayShapePreset.OneMonth ? 1 : preset === WeekdayShapePreset.ThreeMonths ? 3 : 12
  return { from: addCalendarDays(shiftMonth(anchor, -months), 1), to: anchor }
}
export function previousEqualRange(range: WeekdayDateRange): WeekdayDateRange {
  const days = weekdayRangeDays(range)
  return days ? { from: addCalendarDays(range.from, -days), to: addCalendarDays(range.from, -1) } : { ...range }
}
export function dateKeyInTimeZone(value: string | Date, timeZone = WEEKDAY_SALES_TIME_ZONE): string | null {
  const date = value instanceof Date ? value : new Date(value)
  if (!Number.isFinite(date.getTime())) return null
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date)
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find(entry => entry.type === type)?.value
  return part('year') && part('month') && part('day') ? `${part('year')}-${part('month')}-${part('day')}` : null
}

function intersect(left: WeekdayDateRange, right: WeekdaySalesCoverage): WeekdayDateRange | null {
  const range = { from: left.from > right.from ? left.from : right.from, to: left.to < right.to ? left.to : right.to }
  return weekdayRangeDays(range) ? range : null
}
function metricValue(metric: WeekdaySalesMetric, orders: number, turnoverKopecks: number, occurrences: number): number {
  if (metric === WeekdaySalesMetric.Orders) return occurrences ? orders / occurrences : 0
  if (metric === WeekdaySalesMetric.Turnover) return occurrences ? turnoverKopecks / occurrences : 0
  return orders ? turnoverKopecks / orders : 0
}
function quality(observedDays: number, orders: number): WeekdayEvidenceQuality {
  if (observedDays >= 28 && orders >= 50) return WeekdayEvidenceQuality.Sufficient
  if (observedDays >= 14 && orders >= 20) return WeekdayEvidenceQuality.SmallSample
  return WeekdayEvidenceQuality.Insufficient
}
function aggregateRange(daily: readonly WeekdaySalesDailyAggregate[], requested: WeekdayDateRange, coverage: WeekdaySalesCoverage) {
  const effective = intersect(requested, coverage)
  if (!effective) return null
  const rows = new Map(WEEKDAYS.map(day => [day, { occurrences: 0, daysWithSales: 0, orders: 0, turnoverKopecks: 0 }]))
  const observedDays = weekdayRangeDays(effective)
  for (let offset = 0; offset < observedDays; offset += 1) rows.get(weekdayForDate(addCalendarDays(effective.from, offset))!)!.occurrences += 1
  const byDate = new Map(daily.filter(point => point.date >= effective.from && point.date <= effective.to).map(point => [point.date, point]))
  for (const point of byDate.values()) {
    const row = rows.get(weekdayForDate(point.date)!)!
    row.orders += point.orders
    row.turnoverKopecks += point.turnoverKopecks
    if (point.orders > 0) row.daysWithSales += 1
  }
  return { rows, observedDays, effective }
}

export function buildWeekdayShape(daily: readonly WeekdaySalesDailyAggregate[], range: WeekdayDateRange, coverage: WeekdaySalesCoverage, metric: WeekdaySalesMetric): WeekdayShape {
  if (!weekdayRangeDays(range) || !weekdayRangeDays(coverage)) return { availability: 'unavailable', reason: 'invalid_range', metric, range }
  const current = aggregateRange(daily, range, coverage)
  if (!current) return { availability: 'unavailable', reason: 'range_outside_coverage', metric, range }
  const previousRange = previousEqualRange(range)
  const previous = aggregateRange(daily, previousRange, coverage)
  const totalOrders = [...current.rows.values()].reduce((sum, row) => sum + row.orders, 0)
  const totalTurnoverKopecks = [...current.rows.values()].reduce((sum, row) => sum + row.turnoverKopecks, 0)
  if (!totalOrders) return { availability: 'unavailable', reason: 'no_sales', metric, range }
  const overall = metric === WeekdaySalesMetric.Orders ? totalOrders / current.observedDays : metric === WeekdaySalesMetric.Turnover ? totalTurnoverKopecks / current.observedDays : totalTurnoverKopecks / totalOrders
  const totalMetric = metric === WeekdaySalesMetric.Orders ? totalOrders : metric === WeekdaySalesMetric.Turnover ? totalTurnoverKopecks : 0
  const evidenceQuality = quality(current.observedDays, totalOrders)
  const points = WEEKDAYS.map((weekday): WeekdayShapePoint => {
    const row = current.rows.get(weekday)!, prior = previous?.rows.get(weekday)
    const value = metricValue(metric, row.orders, row.turnoverKopecks, row.occurrences)
    const previousValue = prior && prior.occurrences && (metric !== WeekdaySalesMetric.AverageCheck || prior.orders)
      ? metricValue(metric, prior.orders, prior.turnoverKopecks, prior.occurrences) : null
    const rawShare = metric === WeekdaySalesMetric.Orders ? row.orders : metric === WeekdaySalesMetric.Turnover ? row.turnoverKopecks : 0
    return { weekday, ...row, value, previousValue, changeRate: previousValue && previousValue > 0 ? value / previousValue - 1 : null, index: overall ? value / overall : 0, share: totalMetric ? rawShare / totalMetric : 0, smallSample: row.occurrences < 4 || row.orders < 10 || Boolean(prior && (prior.occurrences < 4 || prior.orders < 10)) }
  })
  return {
    availability: 'available', metric, range, previousRange, points, totalOrders, totalTurnoverKopecks,
    calendarDays: weekdayRangeDays(range), observedDays: current.observedDays,
    daysWithSales: points.reduce((sum, point) => sum + point.daysWithSales, 0), quality: evidenceQuality,
    peakWeekday: points.reduce((best, point) => point.value > best.value ? point : best, points[0]).weekday,
  }
}

export function buildWeekdayForecastProfile(daily: readonly WeekdaySalesDailyAggregate[], range: WeekdayDateRange, coverage: WeekdaySalesCoverage): WeekdayForecastProfile {
  const shape = buildWeekdayShape(daily, range, coverage, WeekdaySalesMetric.Turnover)
  if (shape.availability === 'unavailable') return { availability: 'unavailable', reason: shape.reason, range, quality: WeekdayEvidenceQuality.Insufficient, observedDays: 0, totalOrders: 0 }
  if (shape.quality !== WeekdayEvidenceQuality.Sufficient) return { availability: 'unavailable', reason: 'insufficient_shape_evidence', range, quality: shape.quality, observedDays: shape.observedDays, totalOrders: shape.totalOrders }
  return { availability: 'available', range, indexes: Object.fromEntries(shape.points.map(point => [point.weekday, point.index])) as Record<WeekdayKey, number>, quality: shape.quality, observedDays: shape.observedDays, totalOrders: shape.totalOrders }
}

export function weekdayExposure(profile: WeekdayForecastProfile, range: WeekdayDateRange, firstDayWeight = 1, lastDayWeight = 1): number | null {
  if (profile.availability !== 'available' || !profile.indexes) return null
  const days = weekdayRangeDays(range)
  if (!days || firstDayWeight < 0 || firstDayWeight > 1 || lastDayWeight < 0 || lastDayWeight > 1) return null
  let exposure = 0
  for (let offset = 0; offset < days; offset += 1) {
    const weight = days === 1 ? Math.min(firstDayWeight, lastDayWeight) : offset === 0 ? firstDayWeight : offset === days - 1 ? lastDayWeight : 1
    exposure += profile.indexes[weekdayForDate(addCalendarDays(range.from, offset))!] * weight
  }
  return exposure
}

export function boundedWeekdayHistory(daily: readonly WeekdaySalesDailyAggregate[]): WeekdaySalesDailyAggregate[] {
  const sorted = [...daily].sort((left, right) => left.date.localeCompare(right.date))
  const lastDate = sorted.at(-1)?.date
  if (!lastDate) return []
  const firstDate = addCalendarDays(lastDate, -(WEEKDAY_SHAPE_HISTORY_DAYS - 1))
  return sorted.filter(point => point.date >= firstDate)
}
